import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { IonButton, IonIcon } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { NetworkProvider } from '../../providers/network/network';
import { isEmpty } from '../../utils/isBlank';
import { AlertController } from '@ionic/angular';
import { BarcodeScannerService } from '../../../api/barcode-scanner.service';
import { TranslateService } from '@ngx-translate/core';
import { EquipementsProvider } from '../../providers/equipement/equipements.provider';
import { EquipementDto } from '../../../api/models/equipement-dto';

@Component({
  selector: 'scan-equipement',
  templateUrl: './scan-equipement.component.html',
  styleUrl: './scan-equipement.component.css',
  standalone: true,
  imports: [IonButton, IonIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScanEquipementComponent {
  private networkProvider = inject(NetworkProvider);
  private alertCtrl = inject(AlertController);
  private barcodeScannerService = inject(BarcodeScannerService);
  private translateService = inject(TranslateService);
  private equipementsProvider = inject(EquipementsProvider);

  siteId = input<string>();

  isConnected = toSignal(
    this.networkProvider.networkStatusSubject.pipe(
      map(() => {
        return this.networkProvider.isConnected();
      })
    )
  );

  canScanQrCode = computed(() => {
    return Capacitor.isNativePlatform() && this.isConnected();
  });

  equipement = output<EquipementDto>();

  async scanEquipementQrCode() {
    const qrCode = await this.barcodeScannerService.scanCode();

    if (isEmpty(qrCode)) {
      return;
    }

    await this.findEquipementByQrCode(qrCode);
  }

  private async findEquipementByQrCode(qrCode: string) {
    try {
      let equipementDto: EquipementDto;
      if (this.siteId()) {
        equipementDto = await this.equipementsProvider.findEquipementFromSiteByQrCode(this.siteId(), qrCode);
      } else {
        equipementDto = await this.equipementsProvider.findEquipementByQrCode(qrCode);
      }

      this.equipement.emit(equipementDto);
    } catch (e) {
      switch (e.error?.errorCode) {
        case 'NO_EQUIPEMENT_FOUND':
          await this.messageAlert(this.translateService.instant('SCAN_QRCODE_EQUIPEMENT.NO_EQUIPEMENT_FOUND'));
          break;
        case 'SEVERAL_EQUIPEMENTS_FOUND':
          await this.messageAlert(this.translateService.instant('SCAN_QRCODE_EQUIPEMENT.SEVERAL_EQUIPEMENTS_FOUND'));
          break;
        case 'EQUIPEMENT_NOT_IN_SITE':
          await this.messageAlert(this.translateService.instant('SCAN_QRCODE_EQUIPEMENT.EQUIPMENT_NOT_IN_SITE'));
          break;
        default:
          await this.messageAlert(this.translateService.instant('SCAN_QRCODE_EQUIPEMENT.SCAN_ERROR'));
          break;
      }
    }
  }

  private async messageAlert(message: string) {
    const alert = await this.alertCtrl.create({
      header: 'Alerte',
      message: message,
      buttons: ['OK'],
    });
    await alert.present();
  }
}
